/**
 * Handles the following route cases:
 * - /ultimas-interacoes/
 * - /ultimas-interacoes/pagina/2
 * - /ultimas-interacoes/${LAST_COMMENTS.VALID_COMMENT_SOURCES.SLUG}
 * - /ultimas-interacoes/${LAST_COMMENTS.VALID_COMMENT_SOURCES.SLUG}/pagina/2
 *
 * Pages bigger than `LAST_COMMENTS.PAGE_LIMIT` will be dismissed.
 */
import LastCommentsPage from 'components/pages/LastComments';

import {
  getCommentsPageNumber,
  getCommentsSource,
  isCommentsSourceValid,
} from 'lib/lastComments';
import { toLastCommentsLink } from 'lib/links';
import { toSidebarFeaturedOffers } from 'lib/offer';
import { resolvify } from 'lib/promises';

import { getAds } from 'services/ads';
import { getCategoriesList } from 'services/category';
import { getLandingsList } from 'services/landing';
import { getFeaturedOffersList } from 'services/offer';
import { getSuggestedSearchKeywordsList } from 'services/search';
import { getStoresList } from 'services/store';

import APP from 'constants/app';
import LAST_COMMENTS from 'constants/lastComments';
import LINKS from 'constants/links';

const LastComments = (props) => <LastCommentsPage {...props} />;

export const getServerSideProps = async ({
  params: { lastCommentsParams = [] },
}) => {
  const [
    commentsSourceSlug,
    pageParamIfCommentsSourceSlugIsPage,
    pageParamIfCommentsSourceSlugIsValid,
  ] = lastCommentsParams;

  if (!!commentsSourceSlug && !isCommentsSourceValid(commentsSourceSlug)) {
    return { redirect: { destination: LINKS[404], permanent: true } };
  }

  const commentsSource = getCommentsSource(commentsSourceSlug);
  /**
   * If no domain is used as first param, `page` param might be used
   * instead like: /ultimas-interacoes/page/2
   */
  const commentsPage =
    commentsSourceSlug === LAST_COMMENTS.PAGE_SLUG
      ? Number(pageParamIfCommentsSourceSlugIsPage)
      : Number(pageParamIfCommentsSourceSlugIsValid) || null;
  const page = getCommentsPageNumber(commentsPage);

  /**
   * Clean url with bad params like strings and invalid numbers
   */
  if (
    commentsPage > LAST_COMMENTS.PAGE_LIMIT ||
    commentsPage < 0 ||
    (!commentsPage &&
      (pageParamIfCommentsSourceSlugIsPage ||
        pageParamIfCommentsSourceSlugIsValid))
  ) {
    return {
      redirect: {
        destination: toLastCommentsLink(commentsSource?.slug),
        permanent: true,
      },
    };
  }

  try {
    const [
      { recommendedOffers },
      featuredOffers,
      categories,
      stores,
      featuredLandings,
      highlightedLandings,
      {
        data: {
          topAd: [topbarAd = null],
          topbarHighlight: navbarAds,
        },
      },
    ] = await resolvify([
      {
        fn: getSuggestedSearchKeywordsList({ withOffers: true }),
        fallback: { recommendedOffers: [] },
      },
      {
        fn: getFeaturedOffersList(),
        fallback: getFeaturedOffersList.fallback,
      },
      {
        fn: getCategoriesList({ selectedCategories: true }),
        fallback: getCategoriesList.fallback,
      },
      {
        fn: getStoresList({ from: 'topbar' }),
        fallback: getStoresList.fallback,
      },
      {
        fn: getLandingsList({ featured: true }),
        fallback: getLandingsList.fallback,
      },
      {
        fn: getLandingsList({ highlighted: true }),
        fallback: getLandingsList.fallback,
      },
      {
        fn: getAds(),
        fallback: getAds.fallback,
      },
    ]);

    return {
      props: {
        commentsSource,
        commentsPage: page,
        sidebarFeaturedOffers: toSidebarFeaturedOffers(
          recommendedOffers,
          featuredOffers
        ),
        [APP.CTX_OPTIONAL_PROPS]: {
          categories,
          featuredLandings,
          highlightedLandings,
          navbarAds,
          stores,
          topbarAd,
        },
      },
    };
  } catch {
    return { props: { commentsSource, commentsPage: page } };
  }
};

export default LastComments;
